/**
 * @author      yontaek@zumper.com (Yon Taek Chung)
 * @copyright   Copyright (c) 2022, Zumper
 * @description Filter constants
 */

import type { FilterTypes } from '../types/filter'

export const FILTER_PANEL = 'Filter Panel'

export const APARTMENTS = 'apartments'
export const CONDOS = 'condos'
export const HOUSES = 'houses'
export const ROOMS = 'rooms'
export const PROPERTY_TYPES = [APARTMENTS, CONDOS, HOUSES, ROOMS] as const

export const DEFAULT_SORT_OPTION = 'relevance1'
export const NEWEST_SORT_OPTION = 'newest'
export const OLDEST_SORT_OPTION = 'listed_on'
export const POPULAR_SORT_OPTION = 'popular'
export const PRICE_SORT_ASC_OPTION = 'price'
export const PRICE_SORT_DESC_OPTION = '-price'
export const RELEVANCE2_SORT_OPTION = 'relevance2'
export const RELEVANCE_SORT_OPTION = 'relevance1'
export const RATING_SORT_ASC_OPTION = 'rating'
export const RATING_SORT_DESC_OPTION = '-rating'

// sort type
export const DEFAULT_SORT = [DEFAULT_SORT_OPTION] as const
export const NEWEST_SORT = [NEWEST_SORT_OPTION] as const
export const OLDEST_SORT = [OLDEST_SORT_OPTION] as const
export const POPULAR_SORT = [POPULAR_SORT_OPTION] as const
export const PRICE_SORT_ASC: [typeof PRICE_SORT_ASC_OPTION] = [PRICE_SORT_ASC_OPTION]
export const PRICE_SORT_DESC = [PRICE_SORT_DESC_OPTION] as const
export const RELEVANCE2_SORT = [RELEVANCE2_SORT_OPTION] as const
export const RELEVANCE_SORT: [typeof RELEVANCE_SORT_OPTION] = [RELEVANCE_SORT_OPTION]

export const ALLOWED_SORT_OPTIONS = new Set([
  DEFAULT_SORT_OPTION,
  NEWEST_SORT_OPTION,
  OLDEST_SORT_OPTION,
  POPULAR_SORT_OPTION,
  PRICE_SORT_ASC_OPTION,
  PRICE_SORT_DESC_OPTION,
  RELEVANCE2_SORT_OPTION,
  RELEVANCE_SORT_OPTION,
  RATING_SORT_ASC_OPTION,
  RATING_SORT_DESC_OPTION,
])

export const MAX_FILTER_PRICE = 10000
export const MIN_FILTER_PRICE = 0
export const MAX_NIGHTLY_FILTER_PRICE = 1000

export const MAX_FILTER_CUSTOM_PRICE = 1000000
export const MIN_FILTER_CUSTOM_PRICE = 0

export const ACCESSIBLE = 'Accessible'
export const PETS_ALLOWED = 'Pets allowed'
export const FREE_CANCELLATION = 'Free Cancellation'
export const POPULAR_OPTIONS = [PETS_ALLOWED, ACCESSIBLE, FREE_CANCELLATION] as const
/**
 * ref: https://github.com/zumper/zumper_code/issues/14627
 * Since we don't have a city that has more than 600 neighborhoods in a city, setting
 * the limit as 600 to not over-engineer this. THIS NEEDS TO BE ADDRESSED
 */
export const MAX_NEIGHBORHOOD_LIMIT = 600

// BEDROOM CONSTANT
export const ROOM = -1
export const STUDIO = 0
export const ONE_BR = 1
export const TWO_BR = 2
export const THREE_BR = 3
export const FOUR_BR = 4 // 4
export const FIVE_BR = 5 // 4+

export const STUDIO_STR = 'studios'
export const ONE_BR_STR = '1 beds'
export const TWO_BR_STR = '2 beds'
export const THREE_BR_STR = '3 beds'
export const FOUR_PLUS_BR_STR = '4+beds'

// min lease days to be considered short term
export const SHORT_TERM_LEASE_DAYS = 180

// min lease days to have a price per night
export const PRICE_PER_NIGHT_LEASE_DAYS = 30
export const DESKTOP_FILTER_HEIGHT = 310

// these should replace string literals throughout the code base. e.g. bizos/filter.js
export const FILTER_TYPES: FilterTypes = {
  AGE_RESTRICTED: 'ageRestricted',
  AVAILABLE_AFTER: 'availableAfter',
  AVAILABLE_BEFORE: 'availableBefore',
  BEDROOMS: 'bedrooms',
  BATHROOMS: 'bathroomsRange',
  BUILDING_AMENITIES: 'buildingAmenities',
  CATS: 'cats',
  DOGS: 'dogs',
  END_DATE: 'endDate',
  FEATURES: 'features',
  FEEDS: 'feeds',
  FLOORPLANS: 'floorplans',
  HAS_IMAGES: 'hasImages',
  HAS_VIRTUAL_TOURS: 'hasVirtualTours',
  HIDE_NO_VACANCY: 'hideNoVacancy',
  INCOME_RESTRICTED: 'incomeRestricted',
  IS_ZAPPABLE: 'isZappable',
  KEYWORDS: 'keywords',
  LISTING_AMENITIES: 'listingAmenities',
  LONG_TERM: 'longTerm',
  MIN_ACTIVE_UNITS: 'minActiveUnits',
  MAX_LAT: 'maxLat',
  MAX_LNG: 'maxLng',
  MAX_DAYS: 'maxDays',
  MAX_PRICE: 'maxPrice',
  MAX_PRICE_PER_BEDROOM: 'maxPricePerBedroom',
  MAX_SQUARE_FEET: 'maxSquareFeet',
  MIN_BATHROOMS: 'minBathrooms',
  MIN_LAT: 'minLat',
  MIN_LNG: 'minLng',
  MIN_PRICE: 'minPrice',
  MIN_SQUARE_FEET: 'minSquareFeet',
  MM_FROM: 'mmFrom',
  NEIGHBORHOOD_IDS: 'neighborhoodIds',
  PAD: 'pad',
  PETS: 'pets',
  PET_TEXT: 'petText',
  PRICE: 'price',
  PROPERTY_CATEGORIES: {
    name: 'propertyCategories',
    APARTMENT: 'apartment',
    CONDO: 'condo',
    HOUSE: 'house',
    ROOM: 'room',
    OTHER: 'other',
  },
  PROPERTY_TYPES: 'propertyTypes',
  Q: 'Q',
  QS: 'qs',
  SECTION8: 'section8',
  SHORT_TERM: 'shortTerm',
  SORT: 'sort',
  SOURCES: {
    name: 'sources',
    APARTMENT_SEARCH: 'apartmentSearch',
    HOMESUITE: 'homesuite',
    FOR_RENT: 'forRent',
    OTHER: 'other',
  },
  START_DATE: 'startDate',
  STUDENT_HOUSING: 'studentHousing',
  URL: 'url',
}

export const FILTER_FACET_TEXT = {
  PET_FRIENDLY: 'Pet Friendly',
  CHEAP: 'Cheap',
  LUXURY: 'Luxury',
  LOW_INCOME: 'Low Income',
  MOVE_IN_SPECIAL: 'Move In Special',
  UTILITIES_INCLUDED: 'Utilities Included',
  NO_SECURITY_DEPOSIT: 'No Security Deposit',
  FURNISHED: 'Furnished',
  SHORT_TERM: 'Short Term',
} as const

export const VERTICAL_TYPES = {
  CHEAP: 'cheap',
  FURNISHED: 'furnished',
  LOW_INCOME: 'low income',
  LUXURY: 'luxury',
  MOVE_IN_SPECIAL: 'move in special',
  NEW_CONSTRUCTION: 'new construction',
  NO_SECURITY_DEPOSIT: 'no security deposit',
  PET_FRIENDLY: 'pet friendly',
  SHORT_TERM: 'short term',
  UTILITIES_INCLUDED: 'utilities included',
}

export const VERTICAL_TEXT = {
  cheap: 'Cheap',
  furnished: 'Furnished',
  'low-income': 'Low-Income',
  luxury: 'Luxury',
  'move-in-special': 'Move-In Special',
  'no-security-deposit': 'No Security Deposit',
  'pet-friendly': 'Pet-Friendly',
  'short-term': 'Short Term',
  'utilities-included': 'Utilities included',
} as const

export const FILTER_FACET_URL = {
  [FILTER_FACET_TEXT.PET_FRIENDLY]: 'pet-friendly',
  [FILTER_FACET_TEXT.CHEAP]: 'cheap',
  [FILTER_FACET_TEXT.LUXURY]: 'luxury',
  [FILTER_FACET_TEXT.LOW_INCOME]: 'low-income',
  [FILTER_FACET_TEXT.MOVE_IN_SPECIAL]: 'move-in-special',
  [FILTER_FACET_TEXT.UTILITIES_INCLUDED]: 'utilities-included',
  [FILTER_FACET_TEXT.NO_SECURITY_DEPOSIT]: 'no-security-deposit',
  [FILTER_FACET_TEXT.FURNISHED]: 'furnished',
  [FILTER_FACET_TEXT.SHORT_TERM]: 'short-term',
} as const

export const FILTER_ANALYTICS_CATEGORY = 'Filter'
export const FILTER_SUMMARY_ANALYTICS_CATEGORY = 'FilterSummary'

export const MAX_BATHROOM_COUNT = 5

export const OTHERS_FILTERS_ANALYTICS_TEXT = {
  [FILTER_TYPES.HAS_IMAGES]: 'Has Images',
  [FILTER_TYPES.HAS_VIRTUAL_TOURS]: '3D Tour or Video Tour',
  [FILTER_TYPES.MAX_DAYS]: 'Posted Today',
} as const

export const URL_FILTER_QUERY_KEYS = {
  ageRestricted: 'age-restricted',
  availableAfter: 'available-after',
  availableBefore: 'available-before',
  minBathrooms: 'bathrooms',
  bathroomsRange: 'bathrooms-range',
  buildingAmenities: 'building-amenities',
  encodedFilters: 'encoded-filters',
  [FILTER_TYPES.END_DATE]: 'end-date',
  features: 'features',
  feeds: 'feeds',
  floorplans: 'floorplans',
  hasIframe: 'has-iframe',
  hasImages: 'images',
  hasVirtualTours: 'has-virtual-tours',
  hideNoVacancy: 'hide-no-vacancy',
  incomeRestricted: 'restricted-income',
  keywords: 'keywords',
  leaseTerm: 'lease-term',
  listingAmenities: 'listing-amenities',
  luxury: 'luxury',
  minActiveUnits: 'min-active-units',
  maxDays: 'max-days',
  maxPricePerBedroom: 'price-per-bedroom',
  maxSqft: 'max-square-feet',
  minSqft: 'min-square-feet',
  neighborhoodIds: 'neighborhood-ids',
  liveOnlineTours: 'live-online-tours',
  pad: 'pad',
  q: 'Q',
  qs: 'qs',
  recommended: 'recommended',
  section8: 'section8',
  sort: 'sort',
  [FILTER_TYPES.START_DATE]: 'start-date',
  studentHousing: 'student-housing',
  transits: 'transits',
  videoTours: 'video-tours',
  waitlisted: 'waitlisted',
  zappable: 'zappable',
} as const

export interface LEASE_LENGTH_SELECTIONS_TYPE {
  LONG_TERM: 'Long-term'
  SHORT_TERM: 'Short-term'
}

export const LEASE_LENGTH_SELECTIONS: LEASE_LENGTH_SELECTIONS_TYPE = {
  LONG_TERM: 'Long-term',
  SHORT_TERM: 'Short-term',
}

export const ZOOM_STATE = 6
export const FILTER_BAR = 'Filter Bar'
export const FILTER_MODAL_TYPES = {
  ROOMS: 'Rooms',
  PETS: 'Pets',
  PRICE_RANGE: 'Price Range',
  SORT: 'Sort',
} as const

export const PRICE_RANGE = [0, MAX_FILTER_PRICE] as const
export const NIGHTLY_PRICE_RANGE = [0, MAX_NIGHTLY_FILTER_PRICE] as const

export const BATHROOMS_OPTIONS: readonly [
  { ariaLabel: '1 bathroom'; label: '1'; isSelected: boolean; value: 1 },
  { ariaLabel: '2 bathrooms'; label: '2'; isSelected: boolean; value: 2 },
  { ariaLabel: '3 bathrooms'; label: '3'; isSelected: boolean; value: 3 },
  { ariaLabel: '4 bathrooms'; label: '4'; isSelected: boolean; value: 4 },
  {
    ariaLabel: '5 bathrooms or more'
    label: '5+'
    isSelected: boolean
    value: 5
  },
] = [
  { ariaLabel: '1 bathroom', label: '1', isSelected: false, value: 1 },
  { ariaLabel: '2 bathrooms', label: '2', isSelected: false, value: 2 },
  { ariaLabel: '3 bathrooms', label: '3', isSelected: false, value: 3 },
  { ariaLabel: '4 bathrooms', label: '4', isSelected: false, value: 4 },
  {
    ariaLabel: '5 bathrooms or more',
    label: '5+',
    isSelected: false,
    value: 5,
  },
]

export const BEDROOMS_OPTIONS: readonly [
  { ariaLabel: 'studio'; label: 'Studio'; isSelected: boolean; value: 0 },
  { ariaLabel: '1 bedroom'; label: '1'; isSelected: boolean; value: 1 },
  { ariaLabel: '2 bedrooms'; label: '2'; isSelected: boolean; value: 2 },
  { ariaLabel: '3 bedrooms'; label: '3'; isSelected: boolean; value: 3 },
  {
    ariaLabel: '4 bedrooms or more'
    label: '4+'
    isSelected: boolean
    value: 4
  },
] = [
  { ariaLabel: 'studio', label: 'Studio', isSelected: false, value: 0 },
  { ariaLabel: '1 bedroom', label: '1', isSelected: false, value: 1 },
  { ariaLabel: '2 bedrooms', label: '2', isSelected: false, value: 2 },
  { ariaLabel: '3 bedrooms', label: '3', isSelected: false, value: 3 },
  { ariaLabel: '4 bedrooms or more', label: '4+', isSelected: false, value: 4 },
]

export const SORT_FILTER_OPTIONS_SHORT_TERM = [
  {
    label: 'relevance1',
    title: 'Most relevant',
  },
  {
    label: 'newest',
    title: 'Newest first',
  },
  {
    label: 'price',
    title: 'Price: low to high',
  },
  {
    label: '-price',
    title: 'Price: high to low',
  },
] as const

export const SORT_FILTER_OPTIONS = [
  ...SORT_FILTER_OPTIONS_SHORT_TERM,
  {
    label: 'rating',
    title: 'Rating: low to high',
  },
  {
    label: '-rating',
    title: 'Rating: high to low',
  },
] as const

export const AIRBNB_FRIENDLY_LABEL = 'Airbnb-friendly'
export const AIRBNB_BLURB =
  'Show properties that allow residents to earn extra money from hosting on Airbnb part-time. Hosting is subject to rules and restrictions. Ask property management for details.'
